import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Users, UserCheck } from '@/utils/icons';

interface CoachOnboardingPanelProps {
  selectedTeam: any;
  onInviteAthletes: () => void;
  onInviteCoaches: () => void;
  onDismiss: () => void;
}

const CoachOnboardingPanel: React.FC<CoachOnboardingPanelProps> = ({
  selectedTeam,
  onInviteAthletes,
  onInviteCoaches,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState<string>('');

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    });
  };

  const teamName = selectedTeam?.name || 'your team';
  const coachCode = selectedTeam?.coach_code || '';
  const athleteCode = selectedTeam?.athlete_code || '';

  return (
    <div className="space-y-6">
      <div className="cgm-card rounded-2xl p-8 text-center">
        <div className="mx-auto w-16 h-16 rounded-full cgm-list-item flex items-center justify-center mb-4">
          <UserPlus className="w-8 h-8 cgm-icon-data" />
        </div>
        <h2 className="text-3xl font-bold cgm-text-primary mb-2">
          Welcome to {teamName}
        </h2>
        <p className="cgm-text-secondary text-base max-w-xl mx-auto">
          Add your athletes to start tracking readiness, recovery, and injury risk.
          You can paste a whole roster at once.
        </p>

        <button
          onClick={onInviteAthletes}
          className="cgm-button mt-6 px-8 py-3 inline-flex items-center justify-center gap-2 text-base"
        >
          <Users className="w-5 h-5" />
          Invite Athletes
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="cgm-card rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-6 h-6 cgm-icon-success" />
            <h3 className="text-lg font-semibold cgm-text-primary">Athlete join code</h3>
          </div>
          <p className="cgm-text-secondary text-sm mb-4">
            Share this with athletes who'll register themselves
          </p>
          <div className="cgm-code-display p-3 mb-3 text-center">
            <div className="text-2xl font-bold cgm-text-primary tracking-wider font-code">
              {athleteCode || '—'}
            </div>
          </div>
          <button
            onClick={() => copyToClipboard(athleteCode)}
            disabled={!athleteCode}
            className="cgm-button w-full py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copiedCode === athleteCode && athleteCode ? 'Copied!' : 'Copy Code'}
          </button>
        </div>

        <div className="cgm-card rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <UserCheck className="w-6 h-6 cgm-icon-data" />
            <h3 className="text-lg font-semibold cgm-text-primary">Coach join code</h3>
          </div>
          <p className="cgm-text-secondary text-sm mb-4">
            Share this with assistant coaches or staff
          </p>
          <div className="cgm-code-display p-3 mb-3 text-center">
            <div className="text-2xl font-bold cgm-text-primary tracking-wider font-code">
              {coachCode || '—'}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => copyToClipboard(coachCode)}
              disabled={!coachCode}
              className="cgm-button py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copiedCode === coachCode && coachCode ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={onInviteCoaches}
              className="cgm-button py-2"
            >
              Invite by email
            </button>
          </div>
        </div>
      </div>

      <div className="cgm-card rounded-2xl p-6">
        <h3 className="text-lg font-semibold cgm-text-primary mb-2">Connect team devices</h3>
        <p className="cgm-text-secondary text-sm mb-4">
          Hook up team-wide hardware like force plates and GPS trackers
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/connect-catapult-vector')}
            className="cgm-button px-4 py-2"
          >
            Connect Catapult
          </button>
          <button
            onClick={() => navigate('/connect-hawkin')}
            className="cgm-button px-4 py-2"
          >
            Connect Hawkin
          </button>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={onDismiss}
          className="cgm-text-muted hover:cgm-text-primary text-sm underline underline-offset-4 transition-colors"
        >
          Skip — show me the dashboard
        </button>
      </div>
    </div>
  );
};

export default CoachOnboardingPanel;
